import { AlertTriangle, Check, ChevronRight, Clipboard, FileInput, Link2, Radio, RefreshCw, X, ZoomIn } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { PreviewData, WikiPage } from "../types";

interface ArticleProps {
  page: WikiPage;
  sourceState: "live" | "updating" | "error";
  onNavigate: (path: string, hash?: string) => void;
}

function formatDate(value?: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
}

export function Article({ page, sourceState, onNavigate }: ArticleProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const previewTimer = useRef<number | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState<{ data: PreviewData; x: number; y: number } | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const click = async (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const copyButton = target.closest<HTMLButtonElement>("button[data-code-copy]");
      if (copyButton) {
        const code = copyButton.closest("figure")?.querySelector("pre code")?.textContent ?? "";
        await navigator.clipboard.writeText(code);
        const original = copyButton.textContent;
        copyButton.textContent = "已复制";
        setTimeout(() => { copyButton.textContent = original; }, 1400);
        return;
      }
      const wrapButton = target.closest<HTMLButtonElement>("button[data-code-wrap]");
      if (wrapButton) {
        const figure = wrapButton.closest("figure");
        figure?.toggleAttribute("data-wrap");
        wrapButton.textContent = figure?.hasAttribute("data-wrap") ? "不换行" : "换行";
        return;
      }
      const image = target.closest<HTMLImageElement>(".markdown-body img");
      if (image) {
        setLightbox({ src: image.src, alt: image.alt });
        return;
      }
      const link = target.closest<HTMLAnchorElement>("a[data-wiki-path]");
      if (link && !link.classList.contains("missing-link")) {
        event.preventDefault();
        onNavigate(link.dataset.wikiPath!, link.hash);
        return;
      }
      const headingAnchor = target.closest<HTMLAnchorElement>("a.heading-anchor");
      if (headingAnchor) {
        event.preventDefault();
        const url = new URL(location.href);
        url.hash = headingAnchor.hash;
        history.replaceState(history.state, "", url);
        document.getElementById(decodeURIComponent(headingAnchor.hash.slice(1)))?.scrollIntoView({ behavior: "smooth" });
        await navigator.clipboard.writeText(url.toString());
      }
    };
    const enter = (event: MouseEvent) => {
      const link = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[data-wiki-path]");
      if (!link || link.classList.contains("missing-link") || matchMedia("(hover: none)").matches) return;
      clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(async () => {
        const data = await api.preview(link.dataset.wikiPath!).catch(() => null);
        if (!data) return;
        const rect = link.getBoundingClientRect();
        setPreview({ data, x: Math.min(window.innerWidth - 380, Math.max(18, rect.left)), y: Math.min(window.innerHeight - 240, rect.bottom + 10) });
      }, 360);
    };
    const leave = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest("a[data-wiki-path]")) return;
      clearTimeout(previewTimer.current);
      setPreview(null);
    };
    content.addEventListener("click", click);
    content.addEventListener("mouseover", enter);
    content.addEventListener("mouseout", leave);
    return () => {
      content.removeEventListener("click", click);
      content.removeEventListener("mouseover", enter);
      content.removeEventListener("mouseout", leave);
      clearTimeout(previewTimer.current);
    };
  }, [page.html, onNavigate]);

  useEffect(() => {
    if (!lightbox) return;
    const close = (event: KeyboardEvent) => event.key === "Escape" && setLightbox(null);
    addEventListener("keydown", close);
    return () => removeEventListener("keydown", close);
  }, [lightbox]);

  const copyPath = async () => {
    await navigator.clipboard.writeText(page.path);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  return (
    <article className="article-sheet">
      <div className="source-spine" data-state={sourceState}>
        <span className="live-dot" aria-hidden="true" />
        <strong>{sourceState === "live" ? "实时源" : sourceState === "updating" ? "提交更新中" : "实时源异常"}</strong>
        <code>{page.path}</code>
        <span className="source-commit">commit {page.head.slice(0, 8)}</span>
        <button type="button" onClick={copyPath}>{copied ? <Check size={14} /> : <Clipboard size={14} />}{copied ? "已复制" : "复制路径"}</button>
      </div>

      <header className="article-header">
        <div className="article-type"><span>{page.type}</span>{page.reviewed && <span>reviewed {formatDate(page.reviewed)}</span>}</div>
        <h1>{page.title}</h1>
        {page.description && <p className="article-description">{page.description}</p>}
        <div className="article-meta">
          {page.updated && <span>更新 {formatDate(page.updated)}</span>}
          {page.tags.map((tag) => <span className="meta-tag" key={tag}>{tag}</span>)}
        </div>
      </header>

      {page.deprecated && (
        <aside className="deprecated-banner">
          <AlertTriangle size={19} />
          <div><strong>这页已经废弃。</strong>{page.supersededBy && <button type="button" onClick={() => onNavigate(page.supersededBy!)}>转到替代页面 <ChevronRight size={15} /></button>}</div>
        </aside>
      )}

      <div ref={contentRef} className="markdown-body" dangerouslySetInnerHTML={{ __html: page.html }} />

      {page.backlinks.length > 0 && (
        <footer className="backlinks">
          <div className="backlinks-title"><FileInput size={17} /><span>引用此页</span><small>{page.backlinks.length}</small></div>
          <div className="backlinks-grid">
            {page.backlinks.map((link) => (
              <button type="button" key={link.path} onClick={() => onNavigate(link.path)}>
                <strong>{link.title}</strong><code>{link.path}</code>
              </button>
            ))}
          </div>
        </footer>
      )}

      <div className="article-end"><Radio size={15} /><span>你看到的是 Server 当前提交。</span></div>

      {preview && (
        <aside className="link-preview" style={{ left: preview.x, top: preview.y }}>
          <div><Link2 size={14} /><code>{preview.data.path}</code></div>
          <strong>{preview.data.title}</strong>
          <p>{preview.data.excerpt}</p>
        </aside>
      )}

      {lightbox && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label="图片预览" onClick={() => setLightbox(null)}>
          <button type="button" className="icon-button" onClick={() => setLightbox(null)} aria-label="关闭图片预览"><X size={22} /></button>
          <img src={lightbox.src} alt={lightbox.alt} onClick={(event) => event.stopPropagation()} />
          <span><ZoomIn size={15} /> 点击空白处关闭</span>
        </div>
      )}
    </article>
  );
}

export function ArticleSkeleton() {
  return (
    <article className="article-sheet skeleton-sheet" aria-label="正在读取页面">
      <div className="source-spine"><RefreshCw className="spin" size={15} /><strong>读取实时源</strong></div>
      <div className="skeleton-line short" /><div className="skeleton-line title" /><div className="skeleton-line title second" />
      <div className="skeleton-block" /><div className="skeleton-block" /><div className="skeleton-block narrow" />
    </article>
  );
}

export function ArticleError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <article className="article-sheet error-sheet">
      <AlertTriangle size={32} />
      <h1>这页没有读取成功。</h1>
      <p>{message}</p>
      <button type="button" onClick={onRetry}><RefreshCw size={17} />重新读取</button>
    </article>
  );
}
